/**
 * Avatars — Easy Relay brand identity avatar components
 * Sprint 9 Track 1A | Charter v36 Appendix A9
 */

export interface AvatarProps {
  size?: 16 | 22 | 28;
  className?: string;
}

export interface AgentAvatarProps extends AvatarProps {
  role: "planner" | "dev" | "human" | "relay";
}

/**
 * RelayLogoAvatar — Starburst mark for @relay and @adk-relay
 * Sprint 26 Item 2 Task 3: Cropped viewBox shows just the starburst mark
 */
export function RelayLogoAvatar({ size = 22, className }: AvatarProps) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 4,
        overflow: "hidden",
        flexShrink: 0,
        background: "#FFF9E6",
      }}
      className={className}
    >
      <svg
        width={size}
        height={size}
        viewBox="200 280 620 470"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Center starburst glow */}
        <circle cx="512" cy="485" r="45" fill="#F0C430" opacity="0.85" />
        <circle cx="512" cy="485" r="32" fill="#FFF9E6" />
        <path d="M506.954 387.143C505.194 386.585 503.478 385.898 501.82 385.087C495.998 382.287 491.076 377.09 489.084 370.887C487.096 364.776 487.723 358.116 490.815 352.483C493.912 346.834 499.198 342.708 505.43 341.074C534.305 333.489 548.849 379.645 514.048 386.774C514.136 400.458 514.468 417.714 513.908 431.234C514.551 432.918 517.883 432.563 520.076 432.964C530.926 434.947 538.411 439.411 546.457 446.598C554.188 439.4 564.638 427.798 572.388 419.855C573.898 421.526 575.639 422.973 577.557 424.152C570.54 431.392 556.714 444.755 550.677 451.983C557.09 461.586 559.882 467.274 560.828 478.954L604.609 478.951C604.531 480.932 604.394 483.122 604.444 485.088L576.945 484.857C573.557 484.834 563.761 484.666 560.88 485.247C559.095 498.454 556.547 503.768 548.621 514.346C558.182 524.614 568.225 534.377 577.757 544.671C575.945 545.795 574.294 547.160 572.848 548.727C566.788 542.628 560.794 536.463 554.867 530.234C551.778 526.999 547.176 521.883 543.909 519.071C534.055 525.986 526.747 529.947 514.486 530.655L514.544 582.862C512.090 582.689 510.741 583.057 507.894 582.653C507.698 565.317 507.693 547.979 507.879 530.642C494.780 529.472 488.119 525.595 477.888 517.952C468.293 528.053 458.600 538.059 448.810 547.970C447.207 546.361 445.380 544.993 443.385 543.906C453.226 533.720 463.170 523.633 473.215 513.649C466.053 503.818 463.508 497.650 462.228 485.489C446.467 485.000 428.949 485.358 413.122 485.384C413.428 483.283 413.441 481.149 413.163 479.044L462.285 478.946C462.999 468.535 466.153 460.966 471.847 452.457C465.345 444.605 452.879 432.743 445.396 424.612C447.206 423.430 448.847 422.006 450.272 420.380C458.334 429.193 467.779 439.085 476.500 447.291C485.065 438.859 494.821 433.084 507.100 432.379C506.706 427.748 506.903 420.659 506.908 415.821L506.954 387.143Z" fill="#5FC4C0" />
        <path d="M445.396 424.612C434.937 431.443 420.975 429.007 413.447 419.037C405.920 409.067 407.400 394.971 416.834 386.783C426.268 378.594 440.432 379.111 449.244 387.965C458.056 396.819 458.505 410.986 450.272 420.380C448.847 422.006 447.206 423.430 445.396 424.612Z" fill="#1A6B6B" />
        <path d="M572.388 419.855C564.049 410.629 564.258 396.527 572.868 387.553C581.477 378.579 595.559 377.785 605.122 385.735C614.686 393.685 616.478 407.675 609.228 417.779C601.978 427.883 588.151 430.666 577.557 424.152C575.639 422.973 573.898 421.526 572.388 419.855Z" fill="#F4A460" />
        <path d="M577.757 544.671C588.319 538.118 602.152 540.868 609.403 550.963C616.655 561.057 614.845 575.045 605.263 582.961C595.681 590.878 581.604 590.017 573.058 580.992C564.512 571.966 564.420 557.863 572.848 548.727C574.294 547.160 575.945 545.795 577.757 544.671Z" fill="#5FC4C0" />
        <path d="M448.810 547.970C457.544 556.732 458.027 570.754 449.916 580.096C441.805 589.438 427.855 590.929 417.953 583.512C408.050 576.094 405.559 562.288 412.244 551.878C418.929 541.467 432.521 537.988 443.385 543.906C445.380 544.993 447.207 546.361 448.810 547.970Z" fill="#F4A460" />
      </svg>
    </div>
  );
}

/**
 * AgentSpokeAvatar — Spoke pattern for agents, color-coded by role
 * - Relay: branded "R" logo (Track 15)
 * - Planner: teal
 * - Devs: yellow
 * - Human: orange (solid circle, no spokes)
 */
export function AgentSpokeAvatar({ role, size = 22, className }: AgentAvatarProps) {
  // Track 15: @relay gets branded logo instead of spoke avatar
  if (role === "relay") {
    return <RelayLogoAvatar size={size} className={className} />;
  }

  // Pre-flight #7099: All agent avatars #1A6B6B dark teal
  const colorVar = "#1A6B6B";

  // Human lead gets a solid circle, not spokes
  if (role === "human") {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        style={{ flexShrink: 0 }}
      >
        <circle cx="12" cy="12" r="10" fill={colorVar} opacity="0.15" />
        <circle cx="12" cy="12" r="6" fill={colorVar} />
      </svg>
    );
  }

  // Planner and devs get spoke pattern
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" fill={colorVar} opacity="0.12" />
      <circle cx="12" cy="12" r="3" fill={colorVar} />
      {/* 6 spokes radiating from center */}
      <line x1="12" y1="12" x2="12" y2="5" stroke={colorVar} strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="12" x2="17.5" y2="7.5" stroke={colorVar} strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="12" x2="17.5" y2="16.5" stroke={colorVar} strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="12" x2="12" y2="19" stroke={colorVar} strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="12" x2="6.5" y2="16.5" stroke={colorVar} strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="12" x2="6.5" y2="7.5" stroke={colorVar} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Determine agent role from author string
 * Track 15: Added "relay" role for @relay hosted planner
 * Sprint 26 Item 2 Task 3: Added adk-relay recognition
 */
export function roleFromAuthor(author: string): "planner" | "dev" | "human" | "relay" {
  if (author === "relay" || author === "relay-planner" || author === "adk-relay") return "relay";
  if (author === "advisor") return "planner";
  if (author.startsWith("dev-") || author === "claude-code" || author.startsWith("agent-")) return "dev";
  return "human";
}
